import React, { useCallback, useEffect, useRef, useState } from "react";
import { suggestPatients } from "../services/api";

function formatDate(iso) {
    if (!iso) return null;
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
}

export default function PatientAutocomplete({ id, value, onChange, onSelect, inputStyle, inputRef: externalRef, placeholder, autoFocus, onFocused, ehpadId }) {
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [dropdownPos, setDropdownPos] = useState({ top: null, bottom: null, left: 0, width: 0, maxHeight: 300 });
    const debounceRef = useRef(null);
    const wrapperRef = useRef(null);
    const internalRef = useRef(null);
    const inputRef = externalRef || internalRef;

    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
            onFocused?.();
        }
    }, [autoFocus, onFocused, inputRef]);

    const updateDropdownPos = useCallback(() => {
        if (!inputRef.current) return;
        const rect = inputRef.current.getBoundingClientRect();
        const spaceBelow = globalThis.innerHeight - rect.bottom - 8;
        const spaceAbove = rect.top - 8;
        const maxH = 300;
        if (spaceBelow >= Math.min(maxH, 120) || spaceBelow >= spaceAbove) {
            setDropdownPos({ top: rect.bottom + 2, bottom: null, left: rect.left, width: rect.width, maxHeight: Math.max(80, spaceBelow) });
        } else {
            setDropdownPos({ top: null, bottom: globalThis.innerHeight - rect.top + 2, left: rect.left, width: rect.width, maxHeight: Math.max(80, spaceAbove) });
        }
    }, [inputRef]);

    const fetchSuggestions = useCallback((q) => {
        clearTimeout(debounceRef.current);
        if (!ehpadId || !q || q.length < 2) { setSuggestions([]); setOpen(false); return; }
        debounceRef.current = setTimeout(async () => {
            try {
                const { data } = await suggestPatients(q, ehpadId);
                updateDropdownPos();
                setSuggestions(data);
                setOpen(data.length > 0);
                setActiveIndex(-1);
            } catch {
                setSuggestions([]);
                setOpen(false);
            }
        }, 200);
    }, [ehpadId, updateDropdownPos]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleChange = (e) => {
        onChange(e.target.value);
        fetchSuggestions(e.target.value);
    };

    const handleSelect = (patient) => {
        setOpen(false);
        setSuggestions([]);
        onSelect(patient);
    };

    const handleKeyDown = (e) => {
        if (!open) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex(i => Math.max(i - 1, 0));
        } else if (e.key === "Enter" && activeIndex >= 0) {
            e.preventDefault();
            handleSelect(suggestions[activeIndex]);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    return (
        <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
            <input
                id={id}
                ref={inputRef}
                style={inputStyle}
                placeholder={placeholder}
                value={value || ""}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                autoComplete="off"
            />
            {open && (
                <ul style={{
                    position: "fixed",
                    top: dropdownPos.top ?? undefined,
                    bottom: dropdownPos.bottom ?? undefined,
                    left: dropdownPos.left,
                    width: Math.max(dropdownPos.width, 260),
                    zIndex: 9999,
                    margin: 0,
                    padding: 0,
                    listStyle: "none",
                    background: "white",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    maxHeight: `${dropdownPos.maxHeight}px`,
                    overflowY: "auto"
                }}>
                    {suggestions.map((s, i) => (
                        <li
                            key={s.id ?? `${s.nom}-${s.prenom}-${s.dateNaissance}-${i}`}
                            onMouseDown={() => handleSelect(s)}
                            style={{ background: i === activeIndex ? "#eff6ff" : "white" }}
                        >
                            <button
                                type="button"
                                style={{
                                padding: "8px 12px",
                                fontSize: "13px",
                                cursor: "pointer",
                                background: "transparent",
                                borderBottom: i < suggestions.length - 1 ? "1px solid #f1f5f9" : "none",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "8px",
                                width: "100%",
                                border: 0,
                                textAlign: "left",
                            }}
                            >
                                <span>
                                    <span style={{ fontWeight: 600 }}>{s.nom}</span>
                                    {" "}
                                    <span>{s.prenom}</span>
                                </span>
                                {s.dateNaissance && (
                                    <span style={{ fontSize: "11px", color: "#94a3b8", flexShrink: 0 }}>
                                        {formatDate(s.dateNaissance)}
                                    </span>
                                )}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
